import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { api, formatApiError, EMAIL_PATTERN } from "../../lib/api";
import { SERVICE_CATEGORIES, INDUSTRIES, DURATIONS, BUDGETS, CONTACT_METHODS } from "../../data/options";
import { Field, TextInput, TextArea, SelectInput, CheckRow, FormAlert, SubmissionSuccess } from "./FormKit";

const OPTIONAL = ["start_date", "duration", "jurisdictions", "budget", "phone"];
const req = (label) => ({ required: `${label} is required` });

export const BusinessEnquiryForm = ({ defaultCategory = "", idPrefix = "biz" }) => {
  const { register, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm({
    mode: "onTouched",
    defaultValues: { service_category: defaultCategory, preferred_contact: "Email", marketing_consent: false, privacy_ack: false },
  });
  const [result, setResult] = useState(null);
  const [serverError, setServerError] = useState("");
  const inFlight = useRef(false);
  const id = (n) => `${idPrefix}-${n}`;
  const contact = watch("preferred_contact");

  const onSubmit = async (values) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setServerError("");
    const payload = { ...values };
    OPTIONAL.forEach((k) => { if (!payload[k]) payload[k] = null; });
    try {
      const { data } = await api.post("/enquiries", payload);
      setResult({ ...data, email: values.email });
    } catch (e) {
      setServerError(formatApiError(e));
    } finally {
      inFlight.current = false;
    }
  };

  if (result) {
    return (
      <SubmissionSuccess
        testId={id("success")}
        title="Request received"
        message="Thank you for contacting Trusted Leap. Your request has been received. Our team will review your requirements and contact you regarding the next steps."
        reference={result.reference}
        emailStatus={result.confirmation_email}
        email={result.email}
      />
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6" data-testid={id("form")}>
      <input type="text" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" {...register("website")} />
      <div className="grid gap-6 md:grid-cols-2">
        <Field id={id("full_name")} label="Full name" required error={errors.full_name}>
          <TextInput id={id("full_name")} autoComplete="name" error={errors.full_name} data-testid={id("full-name-input")} {...register("full_name", { ...req("Full name"), minLength: { value: 2, message: "Enter your full name" } })} />
        </Field>
        <Field id={id("email")} label="Business email" required error={errors.email}>
          <TextInput id={id("email")} type="email" autoComplete="email" error={errors.email} data-testid={id("email-input")} {...register("email", { ...req("Business email"), pattern: { value: EMAIL_PATTERN, message: "Enter a valid email address" } })} />
        </Field>
        <Field id={id("company")} label="Company name" required error={errors.company}>
          <TextInput id={id("company")} autoComplete="organization" error={errors.company} data-testid={id("company-input")} {...register("company", req("Company name"))} />
        </Field>
        <Field id={id("job_title")} label="Job title" required error={errors.job_title}>
          <TextInput id={id("job_title")} autoComplete="organization-title" error={errors.job_title} data-testid={id("job-title-input")} {...register("job_title", req("Job title"))} />
        </Field>
        <Field id={id("country")} label="Country or primary jurisdiction" required error={errors.country}>
          <TextInput id={id("country")} autoComplete="country-name" error={errors.country} data-testid={id("country-input")} {...register("country", req("Country"))} />
        </Field>
        <Field id={id("industry")} label="Industry" required error={errors.industry}>
          <SelectInput id={id("industry")} options={INDUSTRIES} placeholder="Select industry" error={errors.industry} data-testid={id("industry-select")} {...register("industry", req("Industry"))} />
        </Field>
        <Field id={id("service_category")} label="Service category" required error={errors.service_category} className="md:col-span-2">
          <SelectInput id={id("service_category")} options={SERVICE_CATEGORIES} placeholder="Select service category" error={errors.service_category} data-testid={id("service-select")} {...register("service_category", req("Service category"))} />
        </Field>
        <Field id={id("description")} label="Brief description of the requirement" required error={errors.description} hint="Include the challenge, expected outcomes and any key constraints. Please avoid sharing highly sensitive information at this stage." className="md:col-span-2">
          <TextArea id={id("description")} error={errors.description} data-testid={id("description-input")} {...register("description", { ...req("Description"), minLength: { value: 30, message: "Please provide at least 30 characters" }, maxLength: { value: 5000, message: "Maximum 5,000 characters" } })} />
        </Field>
        <Field id={id("start_date")} label="Desired start date" error={errors.start_date}>
          <TextInput id={id("start_date")} type="date" data-testid={id("start-date-input")} {...register("start_date")} />
        </Field>
        <Field id={id("duration")} label="Expected engagement duration" error={errors.duration}>
          <SelectInput id={id("duration")} options={DURATIONS} placeholder="Select duration" data-testid={id("duration-select")} {...register("duration")} />
        </Field>
        <Field id={id("jurisdictions")} label="Relevant jurisdiction(s)" hint="e.g. United Kingdom, UAE, Singapore">
          <TextInput id={id("jurisdictions")} data-testid={id("jurisdictions-input")} {...register("jurisdictions")} />
        </Field>
        <Field id={id("budget")} label="Indicative budget range">
          <SelectInput id={id("budget")} options={BUDGETS} placeholder="Select range" data-testid={id("budget-select")} {...register("budget")} />
        </Field>
        <Field id={id("preferred_contact")} label="Preferred method of contact" required error={errors.preferred_contact}>
          <SelectInput id={id("preferred_contact")} options={CONTACT_METHODS} error={errors.preferred_contact} data-testid={id("contact-method-select")} {...register("preferred_contact", req("Preferred contact method"))} />
        </Field>
        {contact && contact !== "Email" && (
          <Field id={id("phone")} label="Phone number" error={errors.phone}>
            <TextInput id={id("phone")} type="tel" autoComplete="tel" data-testid={id("phone-input")} {...register("phone", { maxLength: { value: 40, message: "Too long" } })} />
          </Field>
        )}
      </div>

      <div className="space-y-4 border-t border-slate-200 pt-6">
        <CheckRow
          id={id("privacy_ack")}
          error={errors.privacy_ack}
          data-testid={id("privacy-checkbox")}
          label={<>I have read and acknowledge the <Link to="/privacy-policy" target="_blank" className="text-[#2563EB] underline underline-offset-2">Privacy Policy</Link> and understand how my information will be used to assess this request.<span className="text-[#2563EB]"> *</span></>}
          {...register("privacy_ack", { required: "You must acknowledge the privacy notice" })}
        />
        <CheckRow
          id={id("marketing_consent")}
          data-testid={id("marketing-checkbox")}
          label="Optional: I would like to receive occasional updates and insights from Trusted Leap. I can unsubscribe at any time."
          {...register("marketing_consent")}
        />
      </div>

      <FormAlert message={serverError} testId={id("server-error")} />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-slate-500">Fields marked * are required.</p>
        <button type="submit" disabled={isSubmitting} className="btn-primary" data-testid={id("submit-button")}>
          {isSubmitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting</> : <>Submit a Business Request <ArrowRight className="h-4 w-4" /></>}
        </button>
      </div>
    </form>
  );
};
